import * as React from 'react';
import { FormattedMessage } from 'react-intl';

import HistoryRowControls from 'Components/HistoryRowControls';
import HistorySection from 'Components/HistorySection';
import ToolTip from 'Components/ToolTip';
import CollapseElem from 'Components/Collapse';
import PlatformConditional from 'Components/PlatformConditional';
import HistoryRow from 'Components/HistoryRow';
import HistoryRowVerticalItem from 'Components/HistoryRowVerticalItem';
import HistoryRowSubHeader from 'Components/HistoryRowSubHeader';
import HistoryRowListing from 'Components/HistoryRowListing';
import { Container, Item } from 'Components/Grid';
import HistoryRowSubHeaderBar from 'Components/HistoryRowSubHeaderBar';
import HistoryRowBasicItem from 'Components/HistoryRowBasicItem';

import colors from '../../../../config/theme/colors';
import { convertTrendToIcon } from '../../utils';
import { exists, sortPartialDate } from 'neuro-utils';
import DocumentCreationButton from 'Components/DocumentCreationButton';
import { statementStyle } from '../Components/Statement';
import { EditingLockedContentContext } from 'Containers/EditingLockedContentContext';
import { useAppSelector } from 'Store/index';
import { alzheimerDiagnoses, TAlzheimerDiagnosis } from 'Routes/Diagnosis/utils/definitions';
import { formatPartialDate } from 'Utility/date';
import { fm } from 'Components/FormatMessage';

const StyledSubHeader2 = ({ title }: IStyledRow): React.JSX.Element => <HistoryRowSubHeader header={title} />;

const StyledRow = ({ title, value, condition, id }: IStyledRow): React.JSX.Element => (
  <React.Fragment>
    <span id={id} />
    {condition === true ? <HistoryRowVerticalItem header={title} value={value ?? '-'} /> : undefined}
  </React.Fragment>
);

const LesionsDataHeader = (): React.JSX.Element => (
  <Container style={{ color: colors.tertiaryText }}>
    <Item xs={3}></Item>
    <Item xs={2}>{fm('imaging.lesions')}</Item>
    <Item xs={2}>
      <ToolTip description={fm('imaging.lesionsDelta')} content={<span>&Delta;</span>} hover={true} />
    </Item>
    <Item xs={2}>
      <ToolTip description={fm('imaging.lesionsMaxDiameter')} content={<span>Ø</span>} hover={true} />
    </Item>
    <Item xs={2}>{fm('imaging.trend')}</Item>
  </Container>
);

const LesionsDataRow = ({ rowTitle, document, lesions }: ILesionsDataRow): React.JSX.Element | null => {
  const inputData = lesions.split('.');
  if (!inputData[0] || !inputData[1]) return null;

  const lesionsData = (document as any)[inputData[0]] && (document as any)[inputData[0]][inputData[1]];
  return lesionsData && lesionsData !== null ? (
    <Container style={{ marginTop: '1rem', fontWeight: 600 }} alignItems="center">
      <Item xs={3} style={{ color: colors.tertiaryText, fontWeight: 400 }}>
        {fm(rowTitle)}
      </Item>
      <Item xs={2}>
        {exists(lesionsData.lesions) ? (
          lesionsData.lesions === 'X' ? (
            <ToolTip
              description={fm('imaging.opts.notDoneUnknown.notDone')}
              content={<span>{lesionsData.lesions}</span>}
              hover={true}
            />
          ) : lesionsData.lesions === 'N/A' ? (
            <ToolTip
              description={fm('imaging.opts.notDoneUnknown.unknown')}
              content={<span>{lesionsData.lesions}</span>}
              hover={true}
            />
          ) : (
            lesionsData.lesions
          )
        ) : (
          <ToolTip description={fm('imaging.opts.notEntered')} content={<span>-</span>} hover={true} />
        )}
      </Item>
      <Item xs={2}>
        {exists(lesionsData.lesionsDelta) ? (
          lesionsData.lesionsDelta
        ) : (
          <ToolTip description={fm('imaging.opts.notEntered')} content={<span>-</span>} hover={true} />
        )}
      </Item>
      <Item xs={2}>
        {exists(lesionsData.lesionsMaxDiameter) ? (
          lesionsData.lesionsMaxDiameter
        ) : (
          <ToolTip description={fm('imaging.opts.notEntered')} content={<span>-</span>} hover={true} />
        )}
      </Item>
      <Item xs={2}>
        {lesionsData.lesionsTrend ? (
          convertTrendToIcon(lesionsData.lesionsTrend)
        ) : (
          <ToolTip description={fm('imaging.opts.notEntered')} content={<span>-</span>} hover={true} />
        )}
      </Item>
    </Container>
  ) : null;
};

const MriDataSection = ({ document }: IMriDataSection): React.JSX.Element => {
  const diagDocs = (useAppSelector(s => s.documents.sortedAndMergedDocuments)?.filter(
    doc => doc._type === 'diagnosis',
  ) ?? []) as Array<IDocumentWithTypeV2<'diagnosis'>>;

  const hasAlzDiag = diagDocs.some(d => alzheimerDiagnoses.includes(d.diagnosis as TAlzheimerDiagnosis));

  return (
    <React.Fragment>
      <PlatformConditional platform={'ms'}>
        <React.Fragment>
          {document.brainT2Flair || document.brainT1 || exists(document.brainVolume) || document.brainVolumeTrend ? (
            <React.Fragment>
              <HistoryRowSubHeader header={fm('imaging.brain')} />
              {document.brainT2Flair && (
                <React.Fragment>
                  <HistoryRowSubHeaderBar header={fm('imaging.brainT2Flair')} />
                  <LesionsDataHeader />
                  <LesionsDataRow rowTitle="imaging.totalLesions" document={document} lesions={'brainT2Flair.total'} />
                  <LesionsDataRow
                    rowTitle="imaging.infratentorialLesions"
                    document={document}
                    lesions={'brainT2Flair.infratentorial'}
                  />
                  <LesionsDataRow
                    rowTitle="imaging.supratentorialLesions"
                    document={document}
                    lesions={'brainT2Flair.supratentorial'}
                  />
                  <LesionsDataRow
                    rowTitle="imaging.intraAndJuxtacorticalLesions"
                    document={document}
                    lesions={'brainT2Flair.intraAndJuxtacortical'}
                  />
                </React.Fragment>
              )}
              {document.brainT1 && (
                <div style={{ marginTop: document.brainT2Flair ? '2rem' : undefined }}>
                  <HistoryRowSubHeaderBar header={fm('imaging.brainT1')} />
                  {!document.brainT2Flair && <LesionsDataHeader />}
                  <LesionsDataRow rowTitle="imaging.totalLesions" document={document} lesions={'brainT1.total'} />
                  <LesionsDataRow
                    rowTitle="imaging.gadoliniumEnhancedLesions"
                    document={document}
                    lesions={'brainT1.gadoliniumEnhanced'}
                  />
                </div>
              )}
              {(exists(document.brainVolume) || document.brainVolumeTrend) && (
                <React.Fragment>
                  <Container
                    style={{
                      margin: document.brainT2Flair || document.brainT1 ? '4rem 0 0.5rem 0' : '1rem 0 0.5rem 0',
                    }}
                  >
                    <Item xs={2} style={{ textAlign: 'left', color: colors.tertiaryText }}>
                      {fm('imaging.brainVolume')}
                    </Item>
                  </Container>
                  <Container>
                    <Item
                      xs={1}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        fontWeight: 600,
                      }}
                    >
                      {document.brainVolume}
                      <span style={{ marginRight: '1.7rem' }}>
                        {convertTrendToIcon(document.brainVolumeTrend, true)}
                      </span>
                    </Item>
                  </Container>
                </React.Fragment>
              )}
            </React.Fragment>
          ) : null}
          {document.spinalCordT2 || document.spinalCordT1 ? (
            <React.Fragment>
              <HistoryRowSubHeader header={fm('imaging.spinalCord')} />
              {document.spinalCordT2 && (
                <React.Fragment>
                  <HistoryRowSubHeaderBar header={fm('imaging.spinalCordT2')} />
                  <LesionsDataHeader />
                  <LesionsDataRow rowTitle="imaging.totalLesions" document={document} lesions={'spinalCordT2.total'} />
                </React.Fragment>
              )}
              {document.spinalCordT1 && (
                <div style={{ marginTop: document.spinalCordT2 ? '2rem' : undefined }}>
                  <HistoryRowSubHeaderBar header={fm('imaging.spinalCordT1')} />
                  <LesionsDataRow
                    rowTitle="imaging.gadoliniumEnhancedLesions"
                    document={document}
                    lesions={'spinalCordT1.gadoliniumEnhanced'}
                  />
                </div>
              )}
            </React.Fragment>
          ) : null}
        </React.Fragment>
      </PlatformConditional>
      <PlatformConditional platform={'parkinson'}>
        {!!(document.wereMicroangiopathicChangesInWhiteMatterAbnormallyNumerous || document.atrophy) ? (
          <React.Fragment>
            <StyledSubHeader2 title={fm('imaging.whiteMatterChanges')} />
            <StyledRow
              id={'microAngiopathicHistory'}
              title={fm('imaging.wereMicroangiopathicChangesInWhiteMatterAbnormallyNumerous')}
              value={
                document.wereMicroangiopathicChangesInWhiteMatterAbnormallyNumerous &&
                fm(`imaging.opts.yesFazekas0.${document.wereMicroangiopathicChangesInWhiteMatterAbnormallyNumerous}`)
              }
              condition={true}
            />
            <StyledRow
              id={'fazekasClassificationHistory'}
              title={fm('imaging.wasFazekasClassificationDone')}
              value={
                document.wasFazekasClassificationDone &&
                fm(`imaging.opts.yesNo.${document.wasFazekasClassificationDone}`)
              }
              condition={document.wereMicroangiopathicChangesInWhiteMatterAbnormallyNumerous === 'yes'}
            />
            <StyledRow
              id={'fazekasGradeHistory'}
              title={fm('imaging.fazekasGrade')}
              value={document.fazekasGrade && fm(`imaging.opts.fazekas.${document.fazekasGrade}`)}
              condition={
                document.wereMicroangiopathicChangesInWhiteMatterAbnormallyNumerous === 'yes' &&
                document.wasFazekasClassificationDone === 'yes'
              }
            />
            <StyledRow
              id={'antrophyHistory'}
              title={fm('imaging.atrophy')}
              value={document.atrophy && fm(`imaging.opts.atrophy.${document.atrophy}`)}
              condition={true}
            />
            <StyledRow
              id={'antrophyLocalizationHistory'}
              title={fm('imaging.atrophyLocalization')}
              value={document.atrophyLocalization}
              condition={document.atrophy === 'focal'}
            />
          </React.Fragment>
        ) : (
          <></>
        )}
        {document.otherClinicallyRelevantFindingFound ? (
          <React.Fragment>
            <StyledSubHeader2 title={fm('imaging.otherClinicalRelevant')} />
            <StyledRow
              id={'otherRelevantHistory'}
              title={fm('imaging.otherClinicallyRelevantFindingFound')}
              value={fm(`imaging.opts.yesNo.${document.otherClinicallyRelevantFindingFound}`)}
              condition={true}
            />
            <StyledRow
              title={fm('imaging.whatFinding')}
              value={document.otherClinicallyRelevantFinding}
              condition={document.otherClinicallyRelevantFindingFound === 'yes'}
            />
          </React.Fragment>
        ) : (
          <></>
        )}
        {hasAlzDiag && (document.ariaE || document.ariaH || document.ariaNotes) ? (
          <React.Fragment>
            <StyledSubHeader2 title={fm('imaging.ariaE.title')} />
            <StyledRow
              id={'ariaEFinding'}
              title={fm('imaging.ariaE.finding')}
              value={document.ariaE?.finding ? fm(`imaging.opts.ariaE.${document.ariaE.finding}`) : '-'}
              condition={true}
            />
            <StyledRow
              id={'ariaEAmounts'}
              title={fm('imaging.ariaE.amounts')}
              value={Object.entries(document.ariaE?.amounts ?? {})
                .map(([field, value]) => {
                  if (value || value === 0) {
                    return (
                      <div key={field}>
                        <span>{fm(`imaging.brainAreas.${field}`)}</span>
                        <span>{`: ${value}`}</span>
                      </div>
                    );
                  }
                  return;
                })
                .filter(row => row)}
              condition={!!(document.ariaE?.finding && document.ariaE.finding !== 'no')}
            />
            <StyledSubHeader2 title={fm('imaging.ariaH.title')} />
            <StyledRow
              id={'ariaHFinding'}
              title={fm('imaging.ariaH.finding')}
              value={document.ariaH?.finding ? fm(`general.${document.ariaH.finding}`) : '-'}
              condition={true}
            />
            <StyledRow
              id={'ariaHAmounts'}
              title={fm('imaging.ariaH.amounts')}
              value={Object.entries(document.ariaH?.amounts ?? {})
                .map(([field, value]) => {
                  if (value || value === 0) {
                    return (
                      <div key={field}>
                        <span>{fm(`imaging.brainAreas.${field}`)}</span>
                        <span>{`: ${value}`}</span>
                      </div>
                    );
                  }
                  return;
                })
                .concat([
                  <div key={'totalAmount'}>
                    <span>{fm('imaging.ariaH.totalAmount')}</span>
                    <span>{': '}</span>
                    <span>
                      {Object.values(document.ariaH?.amounts ?? {}).reduce(
                        (prev, curr) => (prev ?? 0) + (curr ?? 0),
                        0,
                      )}
                    </span>
                  </div>,
                ])
                .filter(row => row)}
              condition={!!(document.ariaH?.finding && document.ariaH.finding === 'yes')}
            />
            <StyledRow
              id={'ariaHariaELeak'}
              title={fm('imaging.ariaH.ariaELeak')}
              value={document.ariaH?.ariaELeak ? fm(`general.${document.ariaH.ariaELeak}`) : '-'}
              condition={!!(document.ariaH?.finding && document.ariaH.finding === 'yes')}
            />
            <StyledSubHeader2 title={fm('imaging.ariaH.ariaHOther')} />
            <StyledRow
              id={'ariaHOther'}
              title={fm('imaging.ariaH.ariaHOther')}
              value={document.ariaH?.ariaHOther ? fm(`general.${document.ariaH.ariaHOther}`) : '-'}
              condition={true}
            />
            <StyledRow
              id={'ariaHDiameter'}
              title={fm('imaging.ariaH.diameter')}
              value={document.ariaH?.diameter}
              condition={document.ariaH?.ariaHOther === 'yes'}
            />
            <StyledRow id={'ariaNotes'} title={fm('imaging.ariaNotes')} value={document.ariaNotes} condition={true} />
          </React.Fragment>
        ) : (
          <></>
        )}
      </PlatformConditional>
    </React.Fragment>
  );
};

const Mri = ({ documents, startEdit }: IOwnProps): React.JSX.Element => {
  const showProcedureCodeHeader = documents.some(d => d.procedureCode && d.procedureCode.length > 0);
  const showStatementHeader = documents.some(d => d.statement && d.statement.length > 0);

  const showProcedureCode = (d: IMRI) => d.procedureCode && d.procedureCode.length > 0;
  const showStatement = (d: IMRI) => d.statement && d.statement.length > 0;

  const { openEditing } = React.useContext(EditingLockedContentContext);

  return (
    <React.Fragment>
      <PlatformConditional platform={['sma']}>
        <HistorySection
          headerText={<FormattedMessage id={'imaging.mri'} />}
          newButton={
            <DocumentCreationButton
              name="mri"
              text={'imaging.newmri'}
              onClick={startEdit({}, 'mri')}
              width={15}
              height={3}
              fontSize={14}
              alternate={true}
            />
          }
        >
          <HistoryRowListing
            documents={documents}
            headers={
              <React.Fragment>
                <Item xs={2}>
                  <FormattedMessage id={'general.date'} />
                </Item>
                {showProcedureCodeHeader && (
                  <Item xs={2}>
                    <FormattedMessage id={'imaging.procedureCode'} />
                  </Item>
                )}
                {showStatementHeader && (
                  <Item xs={5}>
                    <FormattedMessage id={'imaging.statement'} />
                  </Item>
                )}
              </React.Fragment>
            }
            contentFormat={d => (
              <React.Fragment>
                <Item xs={2} id={'mriDate'}>
                  {formatPartialDate(d.date)}
                </Item>
                <Item xs={2}>{showProcedureCode(d) ? d.procedureCode : ''}</Item>
                <Item xs={5} style={statementStyle}>
                  {showStatement(d) ? d.statement : ''}
                </Item>

                <Item xs={3}>
                  <HistoryRowControls document={d} startEdit={startEdit} />
                </Item>
              </React.Fragment>
            )}
          />
        </HistorySection>
      </PlatformConditional>
      <PlatformConditional platform={'ms'}>
        <HistorySection
          headerText={<FormattedMessage id={'imaging.mri'} />}
          newButton={
            <DocumentCreationButton
              name="mri"
              text={'imaging.newmri'}
              onClick={startEdit({}, 'mri')}
              width={15}
              height={3}
              fontSize={14}
              alternate={true}
            />
          }
          hasHistoryRows={true}
        >
          <React.Fragment>
            {documents
              .toSorted((n1, n2) => sortPartialDate(n2.date, n1.date) || n2._cdate - n1._cdate)
              .map((d, index) => (
                <React.Fragment key={d._id}>
                  <HistoryRow
                    headerText={formatPartialDate(d.date)}
                    rowButton={
                      <HistoryRowControls
                        document={d}
                        startEdit={
                          d._lockedFor && d._source !== null ? doc => () => openEditing(doc, 'imaging.mri') : startEdit
                        }
                      />
                    }
                    controlsMargin={false}
                  >
                    <React.Fragment>
                      <Container>
                        <Item xs={3}>
                          <HistoryRowBasicItem
                            header={<FormattedMessage id={'general.date'} />}
                            value={d.date ? formatPartialDate(d.date) : '-'}
                          />
                        </Item>
                        <Item xs={2}>
                          <HistoryRowBasicItem
                            header={<FormattedMessage id={'imaging.procedureCode'} />}
                            value={d.procedureCode ?? '-'}
                          />
                        </Item>
                        <Item xs={2}>
                          <HistoryRowBasicItem
                            header={<FormattedMessage id={'imaging.tesla'} />}
                            value={d.tesla ?? '-'}
                          />
                        </Item>
                        <Item xs={3}>
                          <HistoryRowBasicItem
                            header={<FormattedMessage id={'imaging.wasGadoliniumGiven'} />}
                            value={d.wasGadoliniumGiven ? fm(`imaging.opts.yesNoUnknown.${d.wasGadoliniumGiven}`) : '-'}
                          />
                        </Item>
                      </Container>
                      {showStatement(d) && (
                        <div style={{ marginTop: '2rem' }}>
                          <StyledRow
                            id={'statement'}
                            title={fm('imaging.statement')}
                            value={d.statement}
                            condition={true}
                          />
                        </div>
                      )}
                      {index === 0 ? (
                        <MriDataSection document={d} />
                      ) : d.brainT2Flair ||
                        d.brainT1 ||
                        exists(d.brainVolume) ||
                        d.brainVolumeTrend ||
                        d.spinalCordT2 ||
                        d.spinalCordT1 ? (
                        <div style={{ marginTop: '3rem' }}>
                          <CollapseElem
                            localeIDs={{ showMessage: 'imaging.show', hideMessage: 'imaging.hide' }}
                            amount={documents.length}
                          >
                            <div style={{ marginBottom: '1rem' }}>
                              <MriDataSection document={d} />
                            </div>
                          </CollapseElem>
                        </div>
                      ) : null}
                    </React.Fragment>
                  </HistoryRow>
                </React.Fragment>
              ))}
          </React.Fragment>
        </HistorySection>
      </PlatformConditional>
      <PlatformConditional platform={'parkinson'}>
        <HistorySection
          headerText={<FormattedMessage id={'imaging.mri'} />}
          newButton={
            <DocumentCreationButton
              name="mri"
              text={'imaging.newmri'}
              onClick={startEdit({}, 'mri')}
              width={15}
              height={3}
              fontSize={14}
              alternate={true}
            />
          }
          hasHistoryRows={true}
        >
          <React.Fragment>
            {documents
              .toSorted((n1, n2) => sortPartialDate(n2.date, n1.date) || n2._cdate - n1._cdate)
              .map((d, index) => (
                <React.Fragment key={d._id}>
                  <HistoryRow
                    headerText={formatPartialDate(d.date)}
                    rowButton={
                      <HistoryRowControls
                        document={d}
                        startEdit={
                          d._lockedFor && d._source !== null ? doc => () => openEditing(doc, 'imaging.mri') : startEdit
                        }
                      />
                    }
                    controlsMargin={false}
                  >
                    <React.Fragment>
                      {showProcedureCode(d) && (
                        <StyledRow
                          id={'procedureCode'}
                          title={fm('imaging.procedureCode')}
                          value={d.procedureCode}
                          condition={true}
                        />
                      )}
                      <StyledRow
                        id={'tesla'}
                        title={fm('imaging.tesla')}
                        value={d.tesla ?? '-'}
                        condition={!!d.tesla}
                      />
                      {showStatement(d) && (
                        <StyledRow
                          id={'statement'}
                          title={fm('imaging.statementText')}
                          value={d.statement}
                          condition={true}
                        />
                      )}
                      {index === 0 ? (
                        <MriDataSection document={d} />
                      ) : d.wereMicroangiopathicChangesInWhiteMatterAbnormallyNumerous ||
                        d.atrophy ||
                        d.otherClinicallyRelevantFindingFound ||
                        d.ariaE?.finding ||
                        d.ariaH?.finding ||
                        d.ariaH?.ariaHOther ||
                        d.ariaNotes ? (
                        <CollapseElem
                          localeIDs={{ showMessage: 'imaging.show', hideMessage: 'imaging.hide' }}
                          amount={documents.length}
                        >
                          <div style={{ marginBottom: '1rem' }}>
                            <MriDataSection document={d} />
                          </div>
                        </CollapseElem>
                      ) : null}
                    </React.Fragment>
                  </HistoryRow>
                </React.Fragment>
              ))}
          </React.Fragment>
        </HistorySection>
      </PlatformConditional>
      <PlatformConditional platform={'ninmt'}>
        <HistorySection
          headerText={<FormattedMessage id={'imaging.mri'} />}
          newButton={
            <DocumentCreationButton
              name="mri"
              text={'imaging.newmri'}
              onClick={startEdit({}, 'mri')}
              width={15}
              height={3}
              fontSize={14}
              alternate={true}
            />
          }
        >
          <HistoryRowListing
            documents={documents}
            headers={
              <React.Fragment>
                <Item xs={2}>
                  <FormattedMessage id={'general.date'} />
                </Item>
                {showProcedureCodeHeader && (
                  <Item xs={2}>
                    <FormattedMessage id={'imaging.procedureCode'} />
                  </Item>
                )}
                {showStatementHeader && (
                  <Item xs={5}>
                    <FormattedMessage id={'imaging.statement'} />
                  </Item>
                )}
              </React.Fragment>
            }
            contentFormat={d => (
              <React.Fragment>
                <Item xs={2} id={'mriDate'}>
                  {formatPartialDate(d.date)}
                </Item>
                <Item xs={2}>{showProcedureCode(d) ? d.procedureCode : ''}</Item>
                <Item xs={5} style={statementStyle}>
                  {showStatement(d) ? d.statement : ''}
                </Item>
                <Item xs={3}>
                  <HistoryRowControls document={d} startEdit={startEdit} />
                </Item>
              </React.Fragment>
            )}
          />
        </HistorySection>
      </PlatformConditional>

      <PlatformConditional platform={'polyneuro'}>
        <HistorySection
          headerText={<FormattedMessage id={'imaging.mri'} />}
          newButton={
            <DocumentCreationButton
              name="mri"
              text={'imaging.newmri'}
              onClick={startEdit({}, 'mri')}
              width={15}
              height={3}
              fontSize={14}
              alternate={true}
            />
          }
        >
          <HistoryRowListing
            documents={documents}
            headers={
              <React.Fragment>
                <Item xs={2}>
                  <FormattedMessage id={'general.date'} />
                </Item>

                <Item xs={3}>
                  <FormattedMessage id={'imaging.polyneuro.imagingRegions'} />
                </Item>

                {showProcedureCodeHeader && (
                  <Item xs={2}>
                    <FormattedMessage id={'imaging.procedureCode'} />
                  </Item>
                )}
                {showStatementHeader && (
                  <Item xs={2}>
                    <FormattedMessage id={'imaging.statement'} />
                  </Item>
                )}
                <Item xs={3} />
              </React.Fragment>
            }
            contentFormat={d => (
              <React.Fragment>
                <Item xs={2} id={'mriDate'}>
                  {formatPartialDate(d.date)}
                </Item>

                <Item xs={3} id={'imagingRegion'}>
                  {d.imagingRegions
                    ? d.imagingRegions.map(region => {
                        const interpretation = d.interpretations?.find(int => int.region === region)?.interpretation;
                        return (
                          <div key={region} style={{ marginBottom: '1rem' }}>
                            <FormattedMessage id={'imaging.polyneuro.opts.' + region} /> {' - '}
                            {interpretation && <FormattedMessage id={'imaging.polyneuro.opts.' + interpretation} />}
                          </div>
                        );
                      })
                    : '-'}
                </Item>

                <Item xs={2}>{showProcedureCode(d) ? d.procedureCode : ''}</Item>
                <Item xs={2} style={statementStyle}>
                  {showStatement(d) ? d.statement : ''}
                </Item>
                <Item xs={3}>
                  <HistoryRowControls document={d} startEdit={startEdit} />
                </Item>
              </React.Fragment>
            )}
          />
        </HistorySection>
      </PlatformConditional>
    </React.Fragment>
  );
};

interface IStyledRow {
  title: string | React.JSX.Element;
  value?: any;
  condition?: boolean;
  id?: string;
}

interface ILesionsDataRow {
  rowTitle: string;
  document: IDocumentWithTypeV2<'mri'>;
  lesions: string;
}

interface IMriDataSection {
  document: IDocumentWithTypeV2<'mri'>;
}

interface IOwnProps {
  documents: IDocumentWithTypeV2<'mri'>[];
  startEdit: StartEdit;
}

export default Mri;
